import { unsafeCSS, html, nothing } from "lit";
import { state, query } from "lit/decorators.js";
import { c as createFocusTrap } from "../../focus-trap.esm-Ddqmou8S.js";
import { e } from "../../scoped-registry-mixin-2fqf-E5n.js";
import { LeafElement } from "../../components/leaf-element.js";
import { LeafDivider } from "../../components/divider/divider.component.js";
import { LeafBackdrop } from "../../components/backdrop/backdrop.component.js";
import { LeafButton } from "../../components/button/button.component.js";
import { LeafIcon } from "../../components/icon/icon.component.js";
import { I18nController } from "../../utilities/i18n.js";
import { isMobile } from "../../utilities/is-mobile.js";
import { SlotController } from "../../utilities/slot.js";
import { appendStylesheet } from "../../utilities/light-dom-styles.js";
import { CHCHeaderSearch } from "../header-search/header-search.component.js";
const styles = `@charset "UTF-8";
/*------------------------------------*\\
    #RESET
\\*------------------------------------*/
/**
 * Border-Box http:/paulirish.com/2012/box-sizing-border-box-ftw/
 */
*,
::slotted(*),
*:before,
*:after {
  box-sizing: border-box;
}

:host *,
:host *::before,
:host *::after {
  box-sizing: inherit;
}

[hidden] {
  display: none !important;
}

/* ------------------------------------*\\
    #TYPOGRAPHY PLACEHOLDERS
\\*------------------------------------ */
.chc-c-header__menu-secondary-nav-links ::slotted([slot=current-section]),
.chc-c-header__menu-secondary-nav-links ::slotted([slot=current-section-mobile]) {
  font-family: var(--leaf-typography-label-large-font-family);
  font-size: var(--leaf-typography-label-large-font-size);
  font-weight: var(--leaf-typography-label-large-font-weight);
  line-height: var(--leaf-typography-label-large-line-height);
  -webkit-text-decoration: var(--leaf-typography-label-large-text-decoration);
          text-decoration: var(--leaf-typography-label-large-text-decoration);
  text-transform: var(--leaf-typography-label-large-text-transform);
}

.chc-c-header__secondary-nav-left-links ::slotted([slot=current-section]),
.chc-c-header__secondary-nav-left-links ::slotted([slot=current-section-mobile]) {
  font-family: var(--leaf-typography-label-default-font-family);
  font-size: var(--leaf-typography-label-default-font-size);
  font-weight: var(--leaf-typography-label-default-font-weight);
  line-height: var(--leaf-typography-label-default-line-height);
  -webkit-text-decoration: var(--leaf-typography-label-default-text-decoration);
          text-decoration: var(--leaf-typography-label-default-text-decoration);
  text-transform: var(--leaf-typography-label-default-text-transform);
}

.leaf-container-full-width, .iw_columns.large-fullwidth {
  left: 50%;
  margin-left: -50dvw;
  margin-right: -50dvw;
  position: relative;
  right: 50%;
  width: 100dvw;
}

.leaf-grid-container {
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1rem;
  padding-right: 1rem;
  width: 100%;
}
@media (min-width: 35rem) {
  .leaf-grid-container {
    max-width: 540px;
  }
}
@media (min-width: 48rem) {
  .leaf-grid-container {
    max-width: 720px;
  }
}
@media (min-width: 60rem) {
  .leaf-grid-container {
    max-width: 960px;
  }
}
@media (min-width: 75rem) {
  .leaf-grid-container {
    max-width: 1140px;
  }
}
@media (min-width: 87.5rem) {
  .leaf-grid-container {
    max-width: 1320px;
  }
}

.leaf-container-12, .leaf-container-4-1-7, .leaf-container-5-1-6, .leaf-container-7-1-4, .leaf-container-5-7, .leaf-container-7-5, .leaf-container-6-6, .leaf-container-6-1-5, .leaf-container-8-1-3, .leaf-container-4-4-4, .leaf-container-2-8-2, .leaf-container-3-2-2-2-3, .leaf-container-3-2-4-3, .leaf-container-3-3-3-3, .leaf-container-1-10-1, .leaf-container-3-6-3, .leaf-container-8-4, .leaf-container-4-8, .leaf-container-9-3, .leaf-container-3-9 {
  display: grid;
}

/*
  Hiding class, making content visible only to screen readers but not visually ("sr" meaning "screen-reader")
  Is the hidden content read by a screen reader?	- Yes
  Will the hidden element affect the document layout? - No
  Will the hidden element’s box model be rendered? - No
  Does the element detect clicks or focus? - Yes
  https://css-tricks.com/comparing-various-ways-to-hide-things-in-css/#method-5-the-visually-hidden-class
*/
.leaf-container-12 {
  grid-template-columns: minmax(0, 1fr);
}
@media (min-width: 48rem) {
  .leaf-container-4-1-7 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 4 - 16px)) minmax(0, calc(8.3333333333% * 1 - 16px)) minmax(0, calc(8.3333333333% * 7 - 16px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-5-1-6 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 5 - 16px)) minmax(0, calc(8.3333333333% * 1 - 16px)) minmax(0, calc(8.3333333333% * 6 - 16px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-7-1-4 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 7 - 16px)) minmax(0, calc(8.3333333333% * 1 - 16px)) minmax(0, calc(8.3333333333% * 4 - 16px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-5-7 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 5 - 12px)) minmax(0, calc(8.3333333333% * 7 - 12px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-7-5 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 7 - 12px)) minmax(0, calc(8.3333333333% * 5 - 12px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-6-6 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 6 - 12px)) minmax(0, calc(8.3333333333% * 6 - 12px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-6-1-5 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 6 - 16px)) minmax(0, calc(8.3333333333% * 1 - 16px)) minmax(0, calc(8.3333333333% * 5 - 16px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-8-1-3 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 8 - 16px)) minmax(0, calc(8.3333333333% * 1 - 16px)) minmax(0, calc(8.3333333333% * 3 - 16px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-4-4-4 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 4 - 16px)) minmax(0, calc(8.3333333333% * 4 - 16px)) minmax(0, calc(8.3333333333% * 4 - 16px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-2-8-2 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 2 - 16px)) minmax(0, calc(8.3333333333% * 8 - 16px)) minmax(0, calc(8.3333333333% * 2 - 16px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-3-2-2-2-3 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 3 - 19.2px)) minmax(0, calc(8.3333333333% * 2 - 19.2px)) minmax(0, calc(8.3333333333% * 2 - 19.2px)) minmax(0, calc(8.3333333333% * 2 - 19.2px)) minmax(0, calc(8.3333333333% * 3 - 19.2px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-3-2-4-3 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 3 - 18px)) minmax(0, calc(8.3333333333% * 2 - 18px)) minmax(0, calc(8.3333333333% * 4 - 18px)) minmax(0, calc(8.3333333333% * 3 - 18px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-3-3-3-3 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 3 - 18px)) minmax(0, calc(8.3333333333% * 3 - 18px)) minmax(0, calc(8.3333333333% * 3 - 18px)) minmax(0, calc(8.3333333333% * 3 - 18px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-1-10-1 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 1 - 16px)) minmax(0, calc(8.3333333333% * 10 - 16px)) minmax(0, calc(8.3333333333% * 1 - 16px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-3-6-3 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 3 - 16px)) minmax(0, calc(8.3333333333% * 6 - 16px)) minmax(0, calc(8.3333333333% * 3 - 16px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-8-4 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 8 - 12px)) minmax(0, calc(8.3333333333% * 4 - 12px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-4-8 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 4 - 12px)) minmax(0, calc(8.3333333333% * 8 - 12px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-9-3 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 9 - 12px)) minmax(0, calc(8.3333333333% * 3 - 12px));
  }
}
@media (min-width: 48rem) {
  .leaf-container-3-9 {
    grid-gap: var(--leaf-spacing-24);
    grid-template-columns: minmax(0, calc(8.3333333333% * 3 - 12px)) minmax(0, calc(8.3333333333% * 9 - 12px));
  }
}
@media (max-width: 47.999rem) {
  .leaf-container-7-1-4, .leaf-container-4-1-7, .leaf-container-5-7, .leaf-container-7-5, .leaf-container-6-6 {
    grid-template-areas: "row-1" "row-2";
  }
  .leaf-container-7-1-4.container-inverted, .leaf-container-4-1-7.container-inverted, .leaf-container-5-7.container-inverted, .leaf-container-7-5.container-inverted, .leaf-container-6-6.container-inverted {
    /* stylelint-disable-next-line max-nesting-depth */
  }
  .leaf-container-7-1-4.container-inverted > :nth-child(1), .leaf-container-4-1-7.container-inverted > :nth-child(1), .leaf-container-5-7.container-inverted > :nth-child(1), .leaf-container-7-5.container-inverted > :nth-child(1), .leaf-container-6-6.container-inverted > :nth-child(1) {
    grid-area: row-2;
  }
}
.iw_row {
  display: grid;
}
@media (min-width: 48rem) {
  .iw_row {
    gap: var(--leaf-spacing-24);
    grid-template-columns: repeat(12, 1fr);
  }
}

.iw_columns {
  display: grid;
}
.iw_columns.large-fullwidth {
  grid-column: span 12;
}
@media (min-width: 48rem) {
  .iw_columns.large-1 {
    grid-column: span 1;
  }
  .iw_columns.large-2 {
    grid-column: span 2;
  }
  .iw_columns.large-3 {
    grid-column: span 3;
  }
  .iw_columns.large-4 {
    grid-column: span 4;
  }
  .iw_columns.large-5 {
    grid-column: span 5;
  }
  .iw_columns.large-6 {
    grid-column: span 6;
  }
  .iw_columns.large-7 {
    grid-column: span 7;
  }
  .iw_columns.large-8 {
    grid-column: span 8;
  }
  .iw_columns.large-9 {
    grid-column: span 9;
  }
  .iw_columns.large-10 {
    grid-column: span 10;
  }
  .iw_columns.large-11 {
    grid-column: span 11;
  }
  .iw_columns.large-12 {
    grid-column: span 12;
  }
}

leaf-accordion,
leaf-alert,
leaf-card,
leaf-list,
leaf-multimedia-limelight,
leaf-tab,
.leaf-c-content-block,
.leaf-c-picture-image {
  display: block;
  margin-top: var(--leaf-spacing-32);
}
@media (min-width: 48rem) {
  leaf-accordion,
  leaf-alert,
  leaf-card,
  leaf-list,
  leaf-multimedia-limelight,
  leaf-tab,
  .leaf-c-content-block,
  .leaf-c-picture-image {
    margin-top: var(--leaf-spacing-48);
  }
}
@media (min-width: 60rem) {
  leaf-accordion,
  leaf-alert,
  leaf-card,
  leaf-list,
  leaf-multimedia-limelight,
  leaf-tab,
  .leaf-c-content-block,
  .leaf-c-picture-image {
    margin-top: var(--leaf-spacing-64);
  }
}

*,
::before,
::after {
  box-sizing: border-box;
}

:where(html) {
  block-size: 100%;
  font-family: var(--leaf-typography-body-default-font-family);
  font-size: var(--leaf-typography-body-default-font-size);
  font-weight: var(--leaf-typography-body-default-font-weight);
  line-height: var(--leaf-typography-body-default-line-height);
  color: var(--leaf-color-content-default);
  background-color: var(--background-default);
  scrollbar-gutter: stable;
  -webkit-text-size-adjust: none;
     -moz-text-size-adjust: none;
          text-size-adjust: none;
}

:where(:not(:active):focus-visible) {
  outline-offset: 1px;
}

:where(body) {
  /* Improve text rendering */
  -webkit-font-smoothing: antialiased;
  /* https://marco.org/2012/11/15/text-rendering-optimize-legibility */
  text-rendering: optimizeSpeed;
  margin: 0;
  /* Allow percentage-based heights in the application */
  min-block-size: 100%;
  overflow-x: hidden;
}
:where(body).backdrop-active {
  overflow-y: hidden;
}

:where(p, h1, h2, h3, h4, h5, h6) {
  overflow-wrap: break-word;
  margin-block: var(--leaf-spacing-16) 0;
  margin-inline: 0;
  word-break: break-word;
  scroll-margin-top: var(--leaf-spacing-32);
  /* Target WebKit (Safari) browsers */
}
:where(p, h1, h2, h3, h4, h5, h6):not(li p):first-child {
  margin-block-start: 0;
}
:where(p, h1, h2, h3, h4, h5, h6):not(li p):last-child {
  margin-block-end: 0;
}
@supports (-webkit-touch-callout: none) {
  @media (max-width: 47.999rem) {
    :where(p, h1, h2, h3, h4, h5, h6) {
      scroll-margin-top: calc(var(--leaf-spacing-32) * 2);
    }
  }
}

:where(h1, h2, h3, h4, h5, h6):not([slot]) {
  color: var(--leaf-color-content-brand-strong);
}

:where(h1) {
  font-family: var(--leaf-typography-display-default-mobile-font-family);
  font-size: var(--leaf-typography-display-default-mobile-font-size);
  font-weight: var(--leaf-typography-display-default-mobile-font-weight);
  line-height: var(--leaf-typography-display-default-mobile-line-height);
  -webkit-text-decoration: var(--leaf-typography-display-default-mobile-text-decoration);
          text-decoration: var(--leaf-typography-display-default-mobile-text-decoration);
  text-transform: var(--leaf-typography-display-default-mobile-text-transform);
}
@media (min-width: 48rem) {
  :where(h1) {
    font-family: var(--leaf-typography-display-default-font-family);
    font-size: var(--leaf-typography-display-default-font-size);
    font-weight: var(--leaf-typography-display-default-font-weight);
    line-height: var(--leaf-typography-display-default-line-height);
    -webkit-text-decoration: var(--leaf-typography-display-default-text-decoration);
            text-decoration: var(--leaf-typography-display-default-text-decoration);
    text-transform: var(--leaf-typography-display-default-text-transform);
  }
}

:where(h2) {
  font-family: var(--leaf-typography-headline-large-mobile-font-family);
  font-size: var(--leaf-typography-headline-large-mobile-font-size);
  font-weight: var(--leaf-typography-headline-large-mobile-font-weight);
  line-height: var(--leaf-typography-headline-large-mobile-line-height);
  -webkit-text-decoration: var(--leaf-typography-headline-large-mobile-text-decoration);
          text-decoration: var(--leaf-typography-headline-large-mobile-text-decoration);
  text-transform: var(--leaf-typography-headline-large-mobile-text-transform);
}
@media (min-width: 48rem) {
  :where(h2) {
    font-family: var(--leaf-typography-headline-large-font-family);
    font-size: var(--leaf-typography-headline-large-font-size);
    font-weight: var(--leaf-typography-headline-large-font-weight);
    line-height: var(--leaf-typography-headline-large-line-height);
    -webkit-text-decoration: var(--leaf-typography-headline-large-text-decoration);
            text-decoration: var(--leaf-typography-headline-large-text-decoration);
    text-transform: var(--leaf-typography-headline-large-text-transform);
  }
}

:where(h3) {
  font-family: var(--leaf-typography-headline-default-mobile-font-family);
  font-size: var(--leaf-typography-headline-default-mobile-font-size);
  font-weight: var(--leaf-typography-headline-default-mobile-font-weight);
  line-height: var(--leaf-typography-headline-default-mobile-line-height);
  -webkit-text-decoration: var(--leaf-typography-headline-default-mobile-text-decoration);
          text-decoration: var(--leaf-typography-headline-default-mobile-text-decoration);
  text-transform: var(--leaf-typography-headline-default-mobile-text-transform);
}
@media (min-width: 48rem) {
  :where(h3) {
    font-family: var(--leaf-typography-headline-default-font-family);
    font-size: var(--leaf-typography-headline-default-font-size);
    font-weight: var(--leaf-typography-headline-default-font-weight);
    line-height: var(--leaf-typography-headline-default-line-height);
    -webkit-text-decoration: var(--leaf-typography-headline-default-text-decoration);
            text-decoration: var(--leaf-typography-headline-default-text-decoration);
    text-transform: var(--leaf-typography-headline-default-text-transform);
  }
}

:where(h4) {
  font-family: var(--leaf-typography-headline-small-mobile-font-family);
  font-size: var(--leaf-typography-headline-small-mobile-font-size);
  font-weight: var(--leaf-typography-headline-small-mobile-font-weight);
  line-height: var(--leaf-typography-headline-small-mobile-line-height);
  -webkit-text-decoration: var(--leaf-typography-headline-small-mobile-text-decoration);
          text-decoration: var(--leaf-typography-headline-small-mobile-text-decoration);
  text-transform: var(--leaf-typography-headline-small-mobile-text-transform);
}
@media (min-width: 48rem) {
  :where(h4) {
    font-family: var(--leaf-typography-headline-small-font-family);
    font-size: var(--leaf-typography-headline-small-font-size);
    font-weight: var(--leaf-typography-headline-small-font-weight);
    line-height: var(--leaf-typography-headline-small-line-height);
    -webkit-text-decoration: var(--leaf-typography-headline-small-text-decoration);
            text-decoration: var(--leaf-typography-headline-small-text-decoration);
    text-transform: var(--leaf-typography-headline-small-text-transform);
  }
}

:where(h5) {
  font-family: var(--leaf-typography-title-large-mobile-font-family);
  font-size: var(--leaf-typography-title-large-mobile-font-size);
  font-weight: var(--leaf-typography-title-large-mobile-font-weight);
  line-height: var(--leaf-typography-title-large-mobile-line-height);
  -webkit-text-decoration: var(--leaf-typography-title-large-mobile-text-decoration);
          text-decoration: var(--leaf-typography-title-large-mobile-text-decoration);
  text-transform: var(--leaf-typography-title-large-mobile-text-transform);
}
@media (min-width: 48rem) {
  :where(h5) {
    font-family: var(--leaf-typography-title-large-font-family);
    font-size: var(--leaf-typography-title-large-font-size);
    font-weight: var(--leaf-typography-title-large-font-weight);
    line-height: var(--leaf-typography-title-large-line-height);
    -webkit-text-decoration: var(--leaf-typography-title-large-text-decoration);
            text-decoration: var(--leaf-typography-title-large-text-decoration);
    text-transform: var(--leaf-typography-title-large-text-transform);
  }
}

:where(h6) {
  font-family: var(--leaf-typography-title-default-font-family);
  font-size: var(--leaf-typography-title-default-font-size);
  font-weight: var(--leaf-typography-title-default-font-weight);
  line-height: var(--leaf-typography-title-default-line-height);
  -webkit-text-decoration: var(--leaf-typography-title-default-text-decoration);
          text-decoration: var(--leaf-typography-title-default-text-decoration);
  text-transform: var(--leaf-typography-title-default-text-transform);
}

:where(small) {
  font-family: var(--leaf-typography-body-small-font-family);
  font-size: var(--leaf-typography-body-small-font-size);
  font-weight: var(--leaf-typography-body-small-font-weight);
  line-height: var(--leaf-typography-body-small-line-height);
  -webkit-text-decoration: var(--leaf-typography-body-small-text-decoration);
          text-decoration: var(--leaf-typography-body-small-text-decoration);
  text-transform: var(--leaf-typography-body-small-text-transform);
}

:where(u, ins, abbr) {
  text-underline-offset: 3px;
  text-decoration-thickness: var(--leaf-border-width-default);
}
@supports (-moz-appearance: none) {
  :where(u, ins, abbr) {
    text-underline-offset: 3px;
  }
}

:where(a[href], area, button, input, label[for], select, summary, textarea, [tabindex]:not([tabindex*="-"])) {
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

:where(form) leaf-input:not([type=hidden]),
:where(form) leaf-select:not([type=hidden]),
:where(form) leaf-textarea:not([type=hidden]),
:where(form) leaf-radiogroup:not([type=hidden]),
:where(form) leaf-checkgroup:not([type=hidden]),
:where(form) leaf-inputgroup:not([type=hidden]),
:where(form) leaf-button:not([type=hidden]),
:where(form) > leaf-label:not([type=hidden]) {
  margin-top: var(--leaf-spacing-24);
  max-width: 100%;
}
:where(form) leaf-button {
  margin-top: var(--leaf-spacing-16);
}
:where(form) leaf-label + leaf-input:not([type=hidden]),
:where(form) leaf-label + leaf-select:not([type=hidden]),
:where(form) leaf-label + leaf-textarea:not([type=hidden]) {
  margin-top: var(--leaf-spacing-4);
}
:where(form) leaf-label + leaf-radiogroup:not([type=hidden]),
:where(form) leaf-label + leaf-checkgroup:not([type=hidden]),
:where(form) leaf-label + leaf-inputgroup:not([type=hidden]) {
  margin-top: var(--leaf-spacing-8);
}

:where([href^=tel]) {
  white-space: nowrap;
}
@media (min-width: 48.001rem) {
  :where([href^=tel]) {
    border-bottom: 0 !important;
    color: inherit !important;
    cursor: default !important;
    font-weight: inherit !important;
    padding-bottom: 0 !important;
    pointer-events: none;
  }
}

:where(a) {
  font-family: var(--leaf-typography-body-default-link-font-family);
  font-size: inherit;
  font-weight: var(--leaf-typography-body-default-link-font-weight);
  line-height: inherit;
  -webkit-text-decoration: var(--leaf-text-decoration-none);
          text-decoration: var(--leaf-text-decoration-none);
  text-transform: var(--leaf-typography-body-default-link-text-transform);
  border-bottom: var(--leaf-border-width-default) solid;
  color: var(--leaf-color-content-link);
  word-break: break-word;
}
:where(a):hover {
  color: var(--leaf-color-content-link-hover);
}
:where(a):focus {
  background-color: var(--leaf-color-bg-active);
  border-color: var(--leaf-color-border-transparent);
  color: var(--leaf-color-content-knockout);
  outline: none;
}
:where(a):where(.leaf-u-link--external)::after {
  background-color: currentColor;
  content: ".";
  margin: 0 0 0 var(--leaf-spacing-4);
  padding: 0 calc(var(--leaf-spacing-8) - var(--leaf-spacing-2));
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' id='svg_1' fill='%23333333' d='m28.667,28.667l-9.334,0l0,-9.334l4.667,0l0,-1.333l-4.667,0c-0.74,0 -1.333,0.6 -1.333,1.333l0,9.334c0,0.733 0.593,1.333 1.333,1.333l9.334,0c0.733,0 1.333,-0.6 1.333,-1.333l0,-4.667l-1.333,0l0,4.667zm-3.334,-10.667l0,1.333l2.394,0l-6.554,6.554l0.94,0.94l6.554,-6.554l0,2.394l1.333,0l0,-4.667l-4.667,0z'/%3E%3C/g%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' id='svg_1' fill='%23333333' d='m28.667,28.667l-9.334,0l0,-9.334l4.667,0l0,-1.333l-4.667,0c-0.74,0 -1.333,0.6 -1.333,1.333l0,9.334c0,0.733 0.593,1.333 1.333,1.333l9.334,0c0.733,0 1.333,-0.6 1.333,-1.333l0,-4.667l-1.333,0l0,4.667zm-3.334,-10.667l0,1.333l2.394,0l-6.554,6.554l0.94,0.94l6.554,-6.554l0,2.394l1.333,0l0,-4.667l-4.667,0z'/%3E%3C/g%3E%3C/svg%3E");
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
}
:where(a):where(.leaf-u-link--lock)::after {
  background-color: currentColor;
  content: ".";
  margin: 0 0 0 var(--leaf-spacing-8);
  padding: 0 calc(var(--leaf-spacing-8) - var(--leaf-spacing-2));
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' d='m28.5,21.25l-0.75,0l0,-1.571c0,-2.169 -1.68,-3.929 -3.75,-3.929c-2.07,0 -3.75,1.76 -3.75,3.929l0,1.571l-0.75,0c-0.825,0 -1.5,0.707 -1.5,1.571l0,7.858c0,0.864 0.675,1.571 1.5,1.571l9,0c0.825,0 1.5,-0.707 1.5,-1.571l0,-7.859c0,-0.864 -0.675,-1.571 -1.5,-1.571l0,0.001zm-6.75,-1.571c0,-1.305 1.005,-2.358 2.25,-2.358s2.25,1.053 2.25,2.358l0,1.571l-4.5,0l0,-1.571zm6.75,11l-9,0l0,-7.859l9,0l0,7.858l0,0.001zm-4.5,-2.359c0.825,0 1.5,-0.707 1.5,-1.571s-0.675,-1.571 -1.5,-1.571s-1.5,0.707 -1.5,1.571s0.675,1.571 1.5,1.571z' fill='%23333333' id='svg_1'/%3E%3C/g%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' d='m28.5,21.25l-0.75,0l0,-1.571c0,-2.169 -1.68,-3.929 -3.75,-3.929c-2.07,0 -3.75,1.76 -3.75,3.929l0,1.571l-0.75,0c-0.825,0 -1.5,0.707 -1.5,1.571l0,7.858c0,0.864 0.675,1.571 1.5,1.571l9,0c0.825,0 1.5,-0.707 1.5,-1.571l0,-7.859c0,-0.864 -0.675,-1.571 -1.5,-1.571l0,0.001zm-6.75,-1.571c0,-1.305 1.005,-2.358 2.25,-2.358s2.25,1.053 2.25,2.358l0,1.571l-4.5,0l0,-1.571zm6.75,11l-9,0l0,-7.859l9,0l0,7.858l0,0.001zm-4.5,-2.359c0.825,0 1.5,-0.707 1.5,-1.571s-0.675,-1.571 -1.5,-1.571s-1.5,0.707 -1.5,1.571s0.675,1.571 1.5,1.571z' fill='%23333333' id='svg_1'/%3E%3C/g%3E%3C/svg%3E");
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: var(--leaf-spacing-40);
          mask-size: var(--leaf-spacing-40);
}
:where(a):where(.leaf-u-link--inverted) {
  color: var(--leaf-color-content-knockout);
}
:where(a):where(.leaf-u-link--inverted):hover {
  color: var(--leaf-color-content-brand-knockout);
}
:where(a):where(.leaf-u-link--inverted):focus {
  background-color: var(--leaf-color-content-knockout);
  color: var(--leaf-color-bg-active);
}
:where(a):where(.leaf-u-link--action) {
  border-bottom: none;
  display: inline-block;
  line-height: var(--leaf-spacing-32);
  margin: var(--leaf-spacing-8) calc(var(--leaf-spacing-48) - var(--leaf-spacing-4));
  margin-left: 0;
  position: relative;
}
:where(a):where(.leaf-u-link--action)::before {
  content: "";
  display: inline-block;
  height: 100%;
  min-height: var(--leaf-spacing-40);
  vertical-align: middle;
}
:where(a):where(.leaf-u-link--action)::after {
  background-color: currentColor;
  bottom: 0;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='white'/%3E%3C/svg%3E");
  height: var(--leaf-spacing-40);
  margin-left: var(--leaf-spacing-4);
  position: absolute;
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23001F99'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='white'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23001F99' stroke-width='2'/%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23001F99'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='white'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23001F99' stroke-width='2'/%3E%3C/svg%3E");
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  width: var(--leaf-spacing-40);
}
:where(a):where(.leaf-u-link--action):focus:after {
  border-radius: 50%;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5.28027' y='5' width='30' height='30' rx='15' fill='%23001F99'/%3E%3Cpath d='M20.2803 12L18.8703 13.41L24.4503 19H12.2803V21H24.4503L18.8703 26.59L20.2803 28L28.2803 20L20.2803 12Z' fill='white'/%3E%3Crect x='39.2803' y='39' width='38' height='38' rx='19' transform='rotate(180 39.2803 39)' stroke='black' stroke-width='2'/%3E%3Crect x='5.28027' y='5' width='30' height='30' rx='15' stroke='%23001F99' stroke-width='2'/%3E%3C/svg%3E");
  -webkit-mask: none;
          mask: none;
}
:where(a):where(.leaf-u-link--action):where(.leaf-u-link--inverted)::after {
  border-radius: 50%;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3C/svg%3E");
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='white'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='white' stroke-width='2'/%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='white'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='white' stroke-width='2'/%3E%3C/svg%3E");
}
:where(a):where(.leaf-u-link--action):where(.leaf-u-link--inverted):hover::after {
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23E6EBFF'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23E6EBFF' stroke-width='2'/%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23E6EBFF'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23E6EBFF' stroke-width='2'/%3E%3C/svg%3E");
}
:where(a):where(.leaf-u-link--action):where(.leaf-u-link--inverted):focus:after {
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23E6EBFF'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='white' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23E6EBFF' stroke-width='2'/%3E%3C/svg%3E");
  -webkit-mask: none;
          mask: none;
}

:where(span) {
  /* is this needed ? */
  /* https://stackoverflow.com/questions/1556363/weird-span-element-rendering-in-safari-on-iphone */
  -webkit-text-size-adjust: none;
     -moz-text-size-adjust: none;
          text-size-adjust: none;
}

:where(img, svg, video, canvas, audio, iframe, embed, object) {
  display: block;
}

:where(img, svg, video) {
  max-inline-size: 100%;
  block-size: auto;
}

:where(strong, b, dt, summary) {
  font-weight: var(--leafdcws-font-weight-bold);
}

:where(p, address, ol, ul, dl, dt:not(:first-of-type)) {
  margin-block-start: var(--leaf-spacing-16);
  margin-block-end: 0;
}

:where(address) {
  font-style: normal;
}

:where(ol, ul) {
  padding-inline-start: var(--leaf-spacing-16);
}

:where(ol ol, ul ul, ol ul, ul ol) {
  margin-block: 0;
}

:where(ul > li > ul > li) {
  list-style-type: circle;
}
:where(ul > li > ul > li) ul > li {
  list-style-type: square;
}
:where(ul > li > ul > li) ul > li ul > li {
  list-style-type: disc;
}

:where(ul) {
  list-style-type: disc;
}

:where(ol) {
  list-style-type: decimal;
}

:where(ol > li > ol) {
  list-style-type: lower-alpha;
}

:where(ol > li > ol > li > ol) {
  list-style-type: lower-roman;
}

:where(li) {
  padding-inline-start: 0;
}

.leaf-c-content-block ul,
.leaf-c-content-block ol {
  display: flex;
  flex-direction: column;
  gap: var(--leaf-spacing-16);
  padding-inline-start: var(--leaf-spacing-32);
}

.visually-hidden {
  clip: rect(0 0 0 0);
  -webkit-clip-path: inset(50%);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

.no-focus-visible-outline:focus-visible {
  outline: none;
}

:host {
  --leafdcws-header-wrapper-mobile-height: 88px;
  --leafdcws-header-logo-desktop-height: 66px;
  --leafdcws-header-logo-mobile-height: 56px;
  --leafdcws-header-logo-desktop-width: 121.26px;
  --leafdcws-header-logo-mobile-width: 102.88px;
}

.chc-c-header__nav-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: var(--leaf-spacing-24);
  padding-right: var(--leaf-spacing-24);
  width: 100%;
}
@media (min-width: 75rem) {
  .chc-c-header__nav-container {
    padding-left: var(--leaf-spacing-16);
    padding-right: var(--leaf-spacing-16);
  }
}

/*
=======
Primary Nav
=======
*/
.chc-c-header__primary-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: var(--leaf-spacing-8);
  padding-top: var(--leaf-spacing-8);
  position: relative;
}
.chc-c-header__primary-nav leaf-divider {
  --leaf-color-border-default: var(--leaf-color-border-strong);
  display: none;
  height: var(--leaf-spacing-32);
  padding-left: var(--leaf-spacing-8);
  padding-right: var(--leaf-spacing-8);
}
@media (min-width: 75rem) {
  .chc-c-header__primary-nav leaf-divider {
    display: block;
  }
}
@media (min-width: 48rem) {
  .chc-c-header__primary-nav {
    padding-bottom: var(--leaf-spacing-16);
    padding-top: var(--leaf-spacing-16);
  }
}

.chc-c-header__primary-nav-right-container {
  display: flex;
}

.chc-c-header__primary-nav-right-links {
  display: none;
  list-style-type: none;
  margin: 0;
  padding: 0;
}
.chc-c-header__primary-nav-right-links ::slotted([slot=primary-nav-right-links]) {
  padding-right: var(--leaf-spacing-24);
}
@media (min-width: 75rem) {
  .chc-c-header__primary-nav-right-links {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
}

.chc-c-header__primary-nav-utilities {
  display: flex;
  align-items: center;
  list-style-type: none;
  margin: 0;
  padding: 0;
}
.chc-c-header__primary-nav-utilities ::slotted([slot=primary-nav-language]) {
  padding-inline-start: var(--leaf-spacing-24) !important;
}
.chc-c-header__primary-nav-utilities .chc-c-header-search__link {
  display: flex;
  align-items: center;
  border-bottom: 0;
  margin-left: var(--leaf-spacing-24);
}
.chc-c-header__primary-nav-utilities .chc-c-header-search__link leaf-icon {
  --icon-height: var(--leaf-spacing-24);
}
@media (min-width: 60rem) {
  .chc-c-header__primary-nav-utilities .chc-c-header-search__link leaf-icon {
    --icon-height: var(--leaf-spacing-24);
  }
}

/*
=======
Secondary Nav
=======
*/
.chc-c-header__secondary-nav {
  background-color: var(--leaf-color-header-bg-secondary);
  position: relative;
}
.chc-c-header__secondary-nav .chc-c-header__nav-menu-button leaf-button::part(base) {
  border-radius: 0;
  padding: 0;
  margin-top: var(--leaf-button-padding-vertical-medium);
  margin-bottom: var(--leaf-button-padding-vertical-medium);
}
.chc-c-header__secondary-nav .chc-c-header__nav-menu-button leaf-button::part(base):focus {
  background-color: var(--leaf-color-content-knockout);
  color: var(--leaf-color-bg-knockout);
}
@media (min-width: 75rem) {
  .chc-c-header__secondary-nav .chc-c-header__nav-menu-button {
    display: none;
  }
}

.chc-c-header__secondary-nav-left-links {
  display: none;
  list-style-type: none;
  margin: 0;
  padding: 0;
}
.chc-c-header__secondary-nav-left-links ::slotted([slot=current-section]),
.chc-c-header__secondary-nav-left-links ::slotted([slot=current-section-mobile]) {
  color: var(--leaf-base-color-neutral-white);
  padding-left: var(--leaf-spacing-16);
  padding-right: var(--leaf-spacing-16);
  padding-top: var(--leaf-spacing-24);
  padding-bottom: var(--leaf-spacing-24);
}
@media (min-width: 75rem) {
  .chc-c-header__secondary-nav-left-links {
    align-items: center;
    display: flex;
    flex: 1;
  }
}

.chc-c-header__secondary-nav-right-links {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  list-style-type: none;
  margin: 0;
  padding: 0;
}
.chc-c-header__secondary-nav-right-links ::slotted([slot=login-button]) {
  padding-top: var(--leaf-spacing-8);
  padding-bottom: var(--leaf-spacing-8);
}
@media (max-width: 74.999rem) {
  .chc-c-header__secondary-nav-right-links {
    margin: var(--leaf-spacing-12) 0;
  }
}

/*
=======
Menu Nav
=======
*/
.chc-c-header__menu-nav {
  background-color: var(--leaf-color-bg-subtle);
  height: 100%;
  left: -600px;
  max-width: 600px;
  overflow: scroll;
  position: fixed;
  top: 0;
  transition: left 0.3s ease;
  min-width: 320px;
  width: 50%;
  z-index: calc(var(--leafdcws-z-index-200) + 2);
}

.chc-c-header__menu-nav-open {
  left: 0;
}
@media (max-width: 600px) {
  .chc-c-header__menu-nav-open {
    width: 90%;
    max-width: 90%;
  }
}

.chc-c-header__menu-nav-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: var(--leaf-spacing-8);
  padding-bottom: var(--leaf-spacing-8);
  padding-left: var(--leaf-spacing-24);
  padding-right: var(--leaf-spacing-24);
}
.chc-c-header__menu-nav-container leaf-button::part(base) {
  border-color: transparent;
}
.chc-c-header__menu-nav-container leaf-button::part(base):focus {
  outline: var(--leaf-border-width-focus) solid var(--leaf-color-border-brand);
  outline-offset: var(--leaf-border-width-focus);
}

.chc-c-header__menu-primary-nav-links {
  background-color: var(--leaf-color-bg-subtle);
  list-style-type: none;
  margin: 0;
  padding: 0;
  padding-top: var(--leaf-spacing-16);
}
.chc-c-header__menu-primary-nav-links ::slotted([slot=primary-nav-right-links]) {
  padding-bottom: var(--leaf-spacing-16);
  padding-inline-start: var(--leaf-spacing-24) !important;
}
.chc-c-header__menu-primary-nav-links ::slotted([slot=mobile-secondary-nav-links]) {
  color: var(--leaf-color-content-link);
  padding-bottom: var(--leaf-spacing-24);
  padding-inline-start: var(--leaf-spacing-24) !important;
}

.chc-c-header__menu-secondary-nav-links {
  background-color: var(--leaf-color-header-bg-default);
  list-style-type: none;
  margin: 0;
  padding: 0;
}
.chc-c-header__menu-secondary-nav-links ::slotted([slot=current-section]),
.chc-c-header__menu-secondary-nav-links ::slotted([slot=current-section-mobile]) {
  border-left: var(--leaf-border-width-large) solid transparent;
  color: var(--leaf-color-content-link);
  padding-top: calc(var(--leaf-spacing-8) + var(--leaf-spacing-4));
  padding-bottom: calc(var(--leaf-spacing-8) + var(--leaf-spacing-4));
  padding-inline-start: calc(var(--leaf-spacing-24) - var(--leaf-border-width-large)) !important;
}
.chc-c-header__menu-secondary-nav-links ::slotted([slot=mobile-secondary-nav-links]) {
  border-left: var(--leaf-border-width-large) solid transparent;
  color: var(--leaf-color-content-link);
  padding-top: var(--leaf-spacing-8);
  padding-bottom: var(--leaf-spacing-8);
  padding-inline-start: calc(var(--leaf-spacing-24) - var(--leaf-border-width-large)) !important;
}`;
const slotStyles = '@charset "UTF-8";\n/* ------------------------------------*\\\n    #TYPOGRAPHY PLACEHOLDERS\n\\*------------------------------------ */\nchc-header [slot=secondary-nav-left-links] > a {\n  font-family: var(--leaf-typography-title-small-font-family);\n  font-size: var(--leaf-typography-title-small-font-size);\n  font-weight: var(--leaf-typography-title-small-font-weight);\n  line-height: var(--leaf-typography-title-small-line-height);\n  -webkit-text-decoration: var(--leaf-typography-title-small-text-decoration);\n          text-decoration: var(--leaf-typography-title-small-text-decoration);\n  text-transform: var(--leaf-typography-title-small-text-transform);\n}\n\n/*\n  Hiding class, making content visible only to screen readers but not visually ("sr" meaning "screen-reader")\n  Is the hidden content read by a screen reader?	- Yes\n  Will the hidden element affect the document layout? - No\n  Will the hidden element’s box model be rendered? - No\n  Does the element detect clicks or focus? - Yes\n  https://css-tricks.com/comparing-various-ways-to-hide-things-in-css/#method-5-the-visually-hidden-class\n*/\nchc-header leaf-accordion-panel-inline::part(header),\nchc-header [slot=showHeader],\nchc-header [slot=hideHeader] {\n  font-family: var(--leaf-typography-label-default-font-family) !important;\n  font-size: var(--leaf-typography-label-default-font-size) !important;\n  font-weight: var(--leaf-typography-label-default-font-weight) !important;\n  line-height: var(--leaf-typography-label-default-line-height) !important;\n  -webkit-text-decoration: var(--leaf-typography-label-default-text-decoration) !important;\n          text-decoration: var(--leaf-typography-label-default-text-decoration) !important;\n  text-transform: var(--leaf-typography-label-default-text-transform) !important;\n}\nchc-header [slot=header-logo] {\n  border-bottom: 0;\n}\nchc-header [slot=header-logo] leaf-icon {\n  --icon-height: var(--leafdcws-header-logo-mobile-height);\n  max-height: var(--leafdcws-header-logo-mobile-height);\n  max-width: var(--leafdcws-header-logo-mobile-width);\n  width: auto;\n}\n@media (min-width: 60rem) {\n  chc-header [slot=header-logo] leaf-icon {\n    --icon-height: var(--leafdcws-header-logo-desktop-height);\n    max-width: var(--leafdcws-header-logo-desktop-width);\n    max-height: var(--leafdcws-header-logo-desktop-height);\n  }\n}\nchc-header [slot=header-logo]:focus {\n  background-color: unset;\n  border: 0;\n  outline: var(--leaf-border-width-focus) solid var(--leaf-color-border-brand);\n  outline-offset: var(--leaf-border-width-focus);\n}\nchc-header [slot=header-logo-mobile] {\n  border: 0;\n}\nchc-header [slot=header-logo-mobile] leaf-icon {\n  --icon-height: var(--leafdcws-header-logo-mobile-height);\n  max-height: var(--leafdcws-header-logo-mobile-height);\n  max-width: var(--leafdcws-header-logo-mobile-width);\n  width: auto;\n}\n@media (min-width: 60rem) {\n  chc-header [slot=header-logo-mobile] leaf-icon {\n    --icon-height: var(--leafdcws-header-logo-desktop-height);\n    max-width: var(--leafdcws-header-logo-desktop-width);\n    max-height: var(--leafdcws-header-logo-desktop-height);\n  }\n}\nchc-header [slot=header-logo-mobile]:focus {\n  background-color: unset;\n  border: 0;\n  outline: var(--leaf-border-width-focus) solid var(--leaf-color-border-brand);\n  outline-offset: var(--leaf-border-width-focus);\n}\n@media (min-width: 75rem) {\n  chc-header [slot=secondary-nav-left-links] {\n    align-self: stretch;\n  }\n}\nchc-header [slot=secondary-nav-left-links] > a {\n  border: var(--leaf-border-width-focus) solid var(--leaf-color-header-menu-border-default);\n  color: var(--leaf-base-color-neutral-white);\n  display: flex;\n  height: 100%;\n  padding-inline-start: calc(var(--leaf-desktop-menu-padding-horizontal-padding) - var(--leaf-border-width-focus)) !important;\n  padding-left: calc(var(--leaf-desktop-menu-padding-horizontal-padding) - var(--leaf-border-width-focus));\n  padding-right: calc(var(--leaf-desktop-menu-padding-horizontal-padding) - var(--leaf-border-width-focus));\n  padding-top: calc(var(--leaf-desktop-menu-padding-vertical-padding) - var(--leaf-border-width-focus) + var(--leaf-border-width-default));\n  padding-bottom: calc(var(--leaf-desktop-menu-padding-vertical-padding) - var(--leaf-border-width-focus) + var(--leaf-border-width-default));\n  position: relative;\n}\n@media (min-width: 60rem) {\n  chc-header [slot=secondary-nav-left-links] > a:hover {\n    background-color: var(--leaf-color-header-menu-bg-hover);\n    border-color: var(--leaf-color-header-menu-border-hover);\n  }\n  chc-header [slot=secondary-nav-left-links] > a:focus {\n    background-color: var(--leaf-base-color-action-blue-140);\n    border-color: var(--leaf-base-color-neutral-white);\n    border-radius: var(--leaf-base-border-radius-8);\n  }\n  chc-header [slot=secondary-nav-left-links] > a:active {\n    background: var(--leaf-color-header-menu-bg-pressed);\n    border-bottom-color: var(--leaf-color-header-menu-border-pressed);\n  }\n}\n@media (min-width: 75rem) {\n  chc-header [slot=secondary-nav-left-links].indicator-active a::before {\n    content: " ";\n    background-color: var(--leaf-color-header-menu-border-active);\n    border-left: var(--leaf-border-width-focus) solid var(--leaf-color-header-menu-border-active);\n    border-right: var(--leaf-border-width-focus) solid var(--leaf-color-header-menu-border-active);\n    height: var(--leaf-border-width-large);\n    position: absolute;\n    bottom: calc(0px - var(--leaf-border-width-focus));\n    left: 0;\n    width: 100%;\n  }\n  chc-header [slot=secondary-nav-left-links].indicator-active a:hover::before, chc-header [slot=secondary-nav-left-links].indicator-active a:focus::before {\n    content: none;\n  }\n}\nchc-header [slot=mobile-secondary-nav-links] {\n  position: relative;\n}\nchc-header [slot=mobile-secondary-nav-links].indicator-active::before {\n  content: " ";\n  border-left: var(--leaf-border-width-large) solid var(--leaf-color-header-menu-content-active);\n  position: absolute;\n  left: calc(0px - var(--leaf-border-width-large));\n  top: 0;\n  width: var(--leaf-border-width-focus);\n  height: 40px;\n}\nchc-header [slot=mobile-secondary-nav-links] a {\n  border-bottom: 0;\n}\nchc-header [slot=mobile-secondary-nav-links] ::part(body-inner) {\n  padding: 0;\n}\nchc-header [slot=mobile-secondary-nav-links] leaf-accordion {\n  margin-top: 0;\n}\nchc-header [slot=mobile-secondary-nav-links] leaf-accordion-panel-inline ul {\n  list-style-type: none;\n  margin: var(--leaf-spacing-12) var(--leaf-spacing-16) 0 0;\n  padding: 0;\n}\nchc-header [slot=mobile-secondary-nav-links] leaf-accordion-panel-inline li {\n  margin-left: var(--leaf-spacing-16);\n  padding-bottom: var(--leaf-spacing-16);\n}\nchc-header [slot=mobile-secondary-nav-links] leaf-accordion-panel-inline li:last-child {\n  padding-bottom: var(--leaf-spacing-0);\n}\n@media (max-width: 47.999rem) {\n  chc-header [slot=mobile-secondary-nav-links] leaf-accordion-panel-inline a:focus {\n    background: var(--leaf-color-bg-opacity-transparent);\n    color: var(--leaf-color-content-link);\n  }\n}\nchc-header [slot=login-button] leaf-button::part(base) {\n  --ripple-bg-color: transparent;\n}\nchc-header [slot=current-section].indicator-active,\nchc-header [slot=current-section-mobile].indicator-active {\n  border-left: var(--leaf-border-width-large) solid var(--leaf-color-header-menu-content-active);\n}\n@media (min-width: 75rem) {\n  chc-header [slot=current-section].indicator-active,\n  chc-header [slot=current-section-mobile].indicator-active {\n    border-left: none;\n  }\n}\nchc-header [slot=current-section-mobile] a {\n  border-bottom: 0;\n}';
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
const LEAF_BUTTON = "leaf-button";
const CHC_HEADER_DROPDOWN = "chc-header-dropdown";
const MATCH_PATH = "match-path";
const INDICATOR_ACTIVE = "indicator-active";
const _CHCHeader = class _CHCHeader extends e(LeafElement) {
  constructor() {
    super();
    this.i18nController = new I18nController(this);
    this.slotController = new SlotController(this);
    this.windowBackdrop = false;
    this.isSearchOpen = false;
    appendStylesheet("chc-header-styles", slotStyles);
  }
  connectedCallback() {
    var _a, _b;
    super.connectedCallback();
    let windowWidth = window.innerWidth;
    window.addEventListener("resize", () => {
      var _a2;
      if (windowWidth !== window.innerWidth) {
        windowWidth = window.innerWidth;
        (_a2 = this.mobileMenuNavElements) == null ? void 0 : _a2.classList.remove("chc-c-header__menu-nav-open");
        this.isSearchOpen = false;
        this.windowBackdrop = false;
        this.requestUpdate();
      }
    });
    this.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        this.toggleMobileMenu();
      }
    });
    (_a = this.shadowRoot) == null ? void 0 : _a.addEventListener("chc-header:search-toggle", this.toggleSearch.bind(this));
    this.querySelectorAll(CHC_HEADER_DROPDOWN).forEach(
      (dropdown) => dropdown.addEventListener("focusout", (event) => {
        if (!dropdown.contains(event.relatedTarget)) {
          dropdown.handleFocusout();
        }
      })
    );
    setTimeout(() => {
      this.updateIndicator("secondary-nav-left-links");
      this.updateIndicator("mobile-secondary-nav-links");
    }, 100);
    const loginButtonMobile = this.querySelector('[slot="login-button-mobile"]');
    if (loginButtonMobile) {
      (_b = loginButtonMobile.querySelector(LEAF_BUTTON)) == null ? void 0 : _b.setAttribute("size", "sm");
    }
  }
  updateIndicator(slotTarget) {
    var _a, _b, _c, _d;
    const currentPath = window.location.pathname;
    const matchPathElements = this.querySelectorAll(`[slot="${slotTarget}"][${MATCH_PATH}]`);
    if (matchPathElements.length > 0) {
      const sorted = Array.from(matchPathElements).sort((a, b) => {
        const pathA = a.getAttribute(MATCH_PATH);
        const pathB = b.getAttribute(MATCH_PATH);
        return pathB.length - pathA.length;
      });
      for (const link of sorted) {
        const path = link.getAttribute(MATCH_PATH);
        if (path && currentPath.startsWith(path)) {
          if (!link.querySelector(CHC_HEADER_DROPDOWN)) {
            link.classList.add(INDICATOR_ACTIVE);
          }
          (_c = (_b = (_a = link.querySelector(CHC_HEADER_DROPDOWN)) == null ? void 0 : _a.shadowRoot) == null ? void 0 : _b.querySelector(".chc-c-header-dropdown__link")) == null ? void 0 : _c.classList.add("chc-c-header-dropdown__link--active");
          break;
        }
      }
      if (slotTarget === "mobile-secondary-nav-links") {
        const hasActiveClass = Array.from(this.querySelectorAll(`[slot="${slotTarget}"]`)).some(
          (element) => element.classList.contains(INDICATOR_ACTIVE)
        );
        if (!hasActiveClass) {
          (_d = this.querySelector('[slot="current-section-mobile"]')) == null ? void 0 : _d.classList.add(INDICATOR_ACTIVE);
        }
      }
    }
  }
  toggleMobileMenu() {
    var _a, _b, _c, _d, _e, _f;
    const expanded = this.mobileMenuNavElements.classList.toggle("chc-c-header__menu-nav-open");
    this.mobileMenuNavElements.setAttribute("aria-expanded", expanded.toString());
    this.windowBackdrop = expanded;
    if (!this.focusTrap) {
      const shadowRoot = this.shadowRoot;
      if (shadowRoot) {
        const mobileMenuElement = shadowRoot.querySelector(".chc-c-header__menu-nav");
        const mobileCloseButtonElement = (_b = (_a = mobileMenuElement.querySelector(LEAF_BUTTON)) == null ? void 0 : _a.shadowRoot) == null ? void 0 : _b.querySelector("button");
        if (mobileMenuElement && mobileCloseButtonElement) {
          this.focusTrap = createFocusTrap(mobileMenuElement, {
            initialFocus: mobileCloseButtonElement,
            allowOutsideClick: true,
            clickOutsideDeactivates: true,
            escapeDeactivates: true,
            tabbableOptions: {
              getShadowRoot: true
            }
          });
        }
      }
    }
    if (expanded) {
      this.focusTrap.activate();
    } else {
      this.focusTrap.deactivate();
      const mobileButtonElement = (_f = (_e = (_d = (_c = this.shadowRoot) == null ? void 0 : _c.querySelector(".chc-c-header__nav-menu-button")) == null ? void 0 : _d.querySelector(LEAF_BUTTON)) == null ? void 0 : _e.shadowRoot) == null ? void 0 : _f.querySelector("a");
      if (mobileButtonElement) {
        mobileButtonElement == null ? void 0 : mobileButtonElement.focus();
      }
    }
  }
  onClickMenu(event) {
    event.preventDefault();
    this.toggleMobileMenu();
  }
  leafBackdropClick() {
    if (this.isSearchOpen) {
      this.toggleSearch();
    } else {
      this.toggleMobileMenu();
    }
  }
  toggleSearch() {
    var _a, _b;
    const ARIA_HIDDEN = "aria-hidden";
    this.windowBackdrop = !this.windowBackdrop;
    this.isSearchOpen = !this.isSearchOpen;
    if (isMobile("xl")) {
      const headerPrimaryNavContainer = (_a = this.shadowRoot) == null ? void 0 : _a.querySelector(".chc-c-header__nav-container");
      if ((headerPrimaryNavContainer == null ? void 0 : headerPrimaryNavContainer.getAttribute(ARIA_HIDDEN)) === "true") {
        headerPrimaryNavContainer.removeAttribute(ARIA_HIDDEN);
      } else {
        headerPrimaryNavContainer == null ? void 0 : headerPrimaryNavContainer.setAttribute(ARIA_HIDDEN, "true");
      }
    } else {
      const headerPrimaryNavContainer = (_b = this.shadowRoot) == null ? void 0 : _b.querySelector(".chc-c-header__primary-nav-right-container");
      if ((headerPrimaryNavContainer == null ? void 0 : headerPrimaryNavContainer.getAttribute("hidden")) === "true") {
        setTimeout(() => {
          headerPrimaryNavContainer == null ? void 0 : headerPrimaryNavContainer.removeAttribute("hidden");
          headerPrimaryNavContainer == null ? void 0 : headerPrimaryNavContainer.removeAttribute(ARIA_HIDDEN);
        }, 300);
      } else {
        headerPrimaryNavContainer == null ? void 0 : headerPrimaryNavContainer.setAttribute("hidden", "true");
        headerPrimaryNavContainer == null ? void 0 : headerPrimaryNavContainer.setAttribute(ARIA_HIDDEN, "true");
      }
    }
  }
  onClickSearch(event) {
    event.preventDefault();
    this.toggleSearch();
  }
  render() {
    if (isMobile("xl"))
      return html` <header class="chc-c-header">
          <nav class="chc-c-header__primary-nav">
            <div class="chc-c-header__nav-container">
              <div class="chc-c-header__logo">
                <slot name="header-logo"></slot>
              </div>
              <div class="chc-c-header__primary-nav-right-container" id="chc-header">
                <ul class="chc-c-header__primary-nav-utilities">
                  ${this.slotController.hasNamedSlot("primary-nav-search-input-label") ? html`
                        <li>
                          <a class="chc-c-header-search__link" href="#" @click=${this.onClickSearch}>
                            ${this.i18nController.term("search")}
                            <leaf-icon
                              slot="icon-after"
                              name="search"
                              label="${this.i18nController.term("search")}"
                            ></leaf-icon>
                          </a>
                        </li>
                      ` : nothing}
                  <slot name="primary-nav-language"></slot>
                </ul>
              </div>
            </div>
            <chc-header-search ?open=${this.isSearchOpen}>
              <slot name="primary-nav-search-input-label"></slot>
            </chc-header-search>
          </nav>
          <nav class="chc-c-header__secondary-nav">
            <div class="chc-c-header__nav-container">
              <p class="visually-hidden">${this.i18nController.term("forms_mode")}</p>
              <div class="chc-c-header__nav-menu-button">
                <leaf-button href="#" @click=${this.onClickMenu}>
                  <leaf-icon slot="icon-before" name="menu" label="${this.i18nController.term("menu")}"></leaf-icon>
                  ${this.i18nController.term("menu")}
                </leaf-button>
              </div>
              <ul class="chc-c-header__secondary-nav-right-links">
                <slot name="login-button-mobile"></slot>
              </ul>
            </div>
          </nav>
          <nav class="chc-c-header__menu-nav" aria-expanded="false">
            <div class="chc-c-header__menu-nav-container">
              <div class="chc-c-header__logo">
                <slot name="header-logo-mobile"></slot>
              </div>
              <leaf-button variant="tertiary" @click=${this.onClickMenu}>
                <leaf-icon slot="icon-before" name="close" label="${this.i18nController.term("close")}"></leaf-icon>
              </leaf-button>
            </div>
            <ul class="chc-c-header__menu-secondary-nav-links">
              <slot name="current-section-mobile"></slot>
              <slot name="mobile-secondary-nav-links"></slot>
            </ul>
            <ul class="chc-c-header__menu-primary-nav-links">
              <slot name="primary-nav-right-links"></slot>
            </ul>
          </nav>
        </header>
        <leaf-backdrop ?active=${this.windowBackdrop} @click=${this.leafBackdropClick}></leaf-backdrop>`;
    else
      return html` <header class="chc-c-header">
          <nav class="chc-c-header__primary-nav">
            <div class="leaf-grid-container chc-c-header__nav-container">
              <div class="chc-c-header__logo">
                <slot name="header-logo"></slot>
              </div>
              <div class="chc-c-header__primary-nav-right-container" aria-label="main" id="chc-header">
                <ul class="chc-c-header__primary-nav-right-links">
                  <slot name="primary-nav-right-links"></slot>
                </ul>
                <leaf-divider variant="vertical"></leaf-divider>
                <ul class="chc-c-header__primary-nav-utilities">
                  ${this.slotController.hasNamedSlot("primary-nav-search-input-label") ? html`
                        <li>
                          <a class="chc-c-header-search__link" href="#" @click=${this.onClickSearch} role="button">
                            Search
                            <leaf-icon
                              slot="icon-after"
                              name="search"
                              label="${this.i18nController.term("search")}"
                            ></leaf-icon>
                          </a>
                        </li>
                      ` : nothing}
                  <li>
                    <slot name="primary-nav-language"></slot>
                  </li>
                </ul>
              </div>
            </div>

            <chc-header-search ?open=${this.isSearchOpen}>
              <div slot="search-header-logo">
                <slot name="header-logo-mobile"></slot>
              </div>
              <slot name="primary-nav-search-input-label"></slot>
            </chc-header-search>
          </nav>
          <nav class="chc-c-header__secondary-nav">
            <div class="leaf-grid-container chc-c-header__nav-container">
              <p class="visually-hidden">${this.i18nController.term("forms_mode")}</p>
              <ul class="chc-c-header__secondary-nav-left-links">
                <slot name="current-section"></slot>
                <slot name="secondary-nav-left-links"></slot>
              </ul>
              <ul class="chc-c-header__secondary-nav-right-links">
                <slot name="login-button"></slot>
              </ul>
            </div>
            <slot name="secondary-nav-left-link-dropdown"></slot>
          </nav>
        </header>
        <leaf-backdrop ?active=${this.windowBackdrop} @click=${this.leafBackdropClick}></leaf-backdrop>`;
  }
};
_CHCHeader.styles = unsafeCSS(styles);
_CHCHeader.elementDefinitions = {
  "chc-header-search": CHCHeaderSearch,
  "leaf-backdrop": LeafBackdrop,
  "leaf-button": LeafButton,
  "leaf-divider": LeafDivider,
  "leaf-icon": LeafIcon
};
let CHCHeader = _CHCHeader;
__decorateClass([
  state()
], CHCHeader.prototype, "windowBackdrop", 2);
__decorateClass([
  state()
], CHCHeader.prototype, "isSearchOpen", 2);
__decorateClass([
  query("leaf-backdrop")
], CHCHeader.prototype, "leafBackdropElement", 2);
__decorateClass([
  query(".chc-c-header__menu-nav")
], CHCHeader.prototype, "mobileMenuNavElements", 2);
export {
  CHCHeader
};
//# sourceMappingURL=header.component.js.map
